/**
 * LoaderPage
 * ページ遷移時Loading画面を制御
 * ページロード完了時に非表示にする
 */
export class LoaderPage {
  /** @type {string[]} 波線カラーバリエーション */
  static WAVE_COLORS = ['is-red', 'is-green', 'is-blue'];

  /**
   * @param {HTMLElement|null} element - Loading要素
   */
  constructor(element = null) {
    this.element = element || document.querySelector('.c-loader-page');
    this.isHiding = false;
    /** @type {ReturnType<typeof setTimeout>|null} */
    this._fallbackTimer = null;
    /** @type {((e: Event) => void)|null} */
    this._animationEndHandler = null;
    /** @type {((e: PageTransitionEvent) => void)|null} */
    this._pageshowHandler = null;
    this.init();
  }

  /**
   * 初期化処理
   * @returns {void}
   */
  init() {
    if (!this.element) return;

    this.setRandomWaveColor();
    this.updateAccessibility();

    this._animationEndHandler = () => this.hideLoader();
    this.element.addEventListener('animationend', this._animationEndHandler, {
      once: true,
    });

    this._fallbackTimer = setTimeout(() => {
      if (!this.isHiding) this.hideLoader();
    }, 15000);

    // bfcache 復帰時は isHiding ガードを迂回して is-active を確実に外す
    // （前回 hideLoader 済み = isHiding=true で hideLoader 経路はスキップされるため）
    this._pageshowHandler = e => {
      if (!e.persisted || !this.element) return;
      this.element.classList.remove('is-active');
      this.updateAccessibility();
    };
    window.addEventListener('pageshow', this._pageshowHandler);
  }

  /**
   * Loading画面を非表示にする
   * @returns {void}
   */
  hideLoader() {
    if (this.isHiding || !this.element) return;

    this.isHiding = true;
    this.element.classList.remove('is-active');
    this.updateAccessibility();

    if (this._fallbackTimer) {
      clearTimeout(this._fallbackTimer);
      this._fallbackTimer = null;
    }
  }

  destroy() {
    if (this._fallbackTimer) {
      clearTimeout(this._fallbackTimer);
      this._fallbackTimer = null;
    }
    if (this._animationEndHandler) {
      this.element?.removeEventListener(
        'animationend',
        this._animationEndHandler,
      );
      this._animationEndHandler = null;
    }
    if (this._pageshowHandler) {
      window.removeEventListener('pageshow', this._pageshowHandler);
      this._pageshowHandler = null;
    }
  }

  /**
   * 波線カラーをランダムに設定
   * @returns {void}
   */
  setRandomWaveColor() {
    if (!this.element) return;

    const waveElement = this.element.querySelector(
      '.c-loader-page__waves--secondary',
    );
    if (!waveElement) return;

    const randomIndex = Math.floor(
      Math.random() * LoaderPage.WAVE_COLORS.length,
    );
    waveElement.classList.add(LoaderPage.WAVE_COLORS[randomIndex]);
  }

  /**
   * アクセシビリティ: aria-busyを更新
   * @returns {void}
   */
  updateAccessibility() {
    if (!this.element) return;

    const isBusy = this.element.classList.contains('is-active');
    this.element.setAttribute('aria-busy', isBusy ? 'true' : 'false');
  }
}
